/**
 * Trash API — wraps /api/trash/* (Wave-D 2026-05-25).
 *
 * Backend lives in apps/chaos-gateway/src/trash.ts. Three endpoints:
 *   GET    /api/trash?scope=&id=
 *   POST   /api/trash/restore  body { type, id }
 *   DELETE /api/trash/purge    body { type, id }   founder-only
 *
 * `voiceInput` and `note` joined 2026-07-26 (migration 0156), when both stopped
 * being hard deletes. They dispatch through the same two routes rather than
 * growing their own, so every caller here keeps working unchanged.
 */
import { apiJson } from './client';

export type TrashScope = 'org' | 'project' | 'board';

export interface TrashCard {
  id: string;
  title: string;
  description: string | null;
  orgId: string | null;
  projectId: string | null;
  boardId: string | null;
  columnId: string | null;
  deletedAt: string;
  updatedAt: string;
}

export interface TrashFile {
  id: string;
  scope: 'org' | 'board' | 'role' | 'card';
  scopeId: string;
  filename: string;
  sizeBytes: number;
  mimeType: string | null;
  description: string | null;
  deletedAt: string;
}

export interface TrashListResponse {
  scope: TrashScope;
  scopeId: string;
  cards: TrashCard[];
  files: TrashFile[];
}

/**
 * What POST /api/trash/restore and DELETE /api/trash/purge accept.
 *
 * A trashed VOICE INPUT still has its audio on disk; purging it is the only
 * thing in the product that unlinks a recording, and the response carries the
 * `bytesReclaimed` the filesystem actually gave back.
 * A trashed NOTE keeps its replies' pointers, so restoring it restores the
 * thread; purging it finally NULLs them, and the response says how many.
 */
export type TrashItemType = 'card' | 'file' | 'voiceInput' | 'note';

export interface TrashPurgeResponse {
  ok: true;
  bytesReclaimed?: number;
  repliesOrphaned?: number;
  audioAlreadyMissing?: boolean;
}

export interface TrashRestoreResponse {
  ok: true;
  /** voiceInput only: the audio is not on disk any more. Nothing in the product
   *  removes it, so this means the host was cleaned by hand. */
  audioMissing?: boolean;
}

export const trashApi = {
  list(scope: TrashScope, scopeId: string): Promise<TrashListResponse> {
    return apiJson<TrashListResponse>(
      `/trash?scope=${encodeURIComponent(scope)}&id=${encodeURIComponent(scopeId)}`,
    );
  },
  restore(type: TrashItemType, id: string): Promise<TrashRestoreResponse> {
    return apiJson<TrashRestoreResponse>(`/trash/restore`, {
      method: 'POST',
      json: { type, id },
    });
  },
  purge(type: TrashItemType, id: string): Promise<TrashPurgeResponse> {
    return apiJson<TrashPurgeResponse>(`/trash/purge`, {
      method: 'DELETE',
      json: { type, id },
    });
  },
};

export const trashKeys = {
  all: ['trash'] as const,
  scope: (scope: TrashScope, id: string) => [...trashKeys.all, scope, id] as const,
};
